import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import type { 
  TeamMemberRole, 
  TournamentStaffRole, 
  OrgMemberRole,
  LeagueStatus,
} from '../types/database';
import { resolveOrganizationDisplayName } from '../lib/organizationDisplayName';

// Define the different context types a user can view as
export type ViewContext = 
  | { type: 'spectator' }
  | { type: 'athlete'; teamId: string; teamName: string; participantId?: string }
  | { type: 'coach'; teamId: string; teamName: string; role: TeamMemberRole }
  | { type: 'organizer'; organizationId: string; organizationName: string; role: OrgMemberRole }
  | { type: 'tournament_staff'; tournamentId: string; tournamentName: string; role: TournamentStaffRole }
  | { type: 'league_manager'; leagueId: string; leagueName: string }
  | { type: 'league_team_manager'; leagueTeamId: string; leagueId: string; teamId: string; teamName: string; leagueName: string; role: 'manager' | 'coach' };

export interface TeamContext {
  id: string;
  slug: string;
  name: string;
  role: TeamMemberRole;
  logoUrl?: string | null;
}

export interface AthleteTeamContext {
  id: string;
  slug: string;
  name: string;
  logoUrl?: string | null;
  participantId?: string;
}

export interface OrganizationContext {
  id: string;
  name: string;
  role: OrgMemberRole;
  logoUrl?: string | null;
}

export interface TournamentContext {
  id: string;
  name: string;
  role: TournamentStaffRole;
  organizationName?: string;
}

export interface LeagueContext {
  id: string;
  name: string;
  slug: string;
  status: LeagueStatus;
  logoUrl?: string | null;
  organizationName?: string;
}

export interface LeagueTeamManagerContext {
  leagueTeamId: string;
  leagueId: string;
  leagueName: string;
  teamId: string;
  teamName: string;
  role: 'manager' | 'coach';
  canManageRoster: boolean;
  canRegisterTournaments: boolean;
  canViewFinancials: boolean;
  teamLogoUrl?: string | null;
}

interface RoleContextType {
  // Current active context
  activeContext: ViewContext;
  setActiveContext: (context: ViewContext) => void;
  
  // Available contexts for the user
  availableTeams: TeamContext[];
  availableAthleteTeams: AthleteTeamContext[];
  availableOrganizations: OrganizationContext[];
  availableTournaments: TournamentContext[];
  availableLeagues: LeagueContext[];
  availableManagedTeams: LeagueTeamManagerContext[];
  
  // Loading state
  loading: boolean;
  
  // Convenience checks
  isViewingAsSpectator: boolean;
  isViewingAsAthlete: boolean;
  isViewingAsCoach: boolean;
  isViewingAsOrganizer: boolean;
  isViewingAsTournamentStaff: boolean;
  isViewingAsLeagueManager: boolean;
  isViewingAsLeagueTeamManager: boolean;
  
  // Get the current team/org/tournament/league if viewing as that role
  currentAthleteTeam: AthleteTeamContext | null;
  currentTeam: TeamContext | null;
  currentOrganization: OrganizationContext | null;
  currentTournament: TournamentContext | null;
  currentLeague: LeagueContext | null;
  currentManagedTeam: LeagueTeamManagerContext | null;
  
  // Refresh available contexts
  refreshContexts: () => Promise<void>;
  
  // Check if user has any management roles
  hasAnyManagementRole: boolean;
  
  // Check if user is an athlete on any team
  isAthlete: boolean;
  
  // Check if user manages any leagues
  hasLeagues: boolean;
  
  // Check if user manages any league teams
  hasManagedTeams: boolean;
}

const STORAGE_KEY = 'tournament_platform_active_context';
const DEFAULT_STORAGE_KEY = 'tournament_platform_default_context';

function getActiveContextStorageKey(userId: string): string {
  return `${STORAGE_KEY}_${userId}`;
}

function readSavedActiveContext(userId: string): ViewContext | null {
  const userKey = getActiveContextStorageKey(userId);
  const saved = localStorage.getItem(userKey) ?? localStorage.getItem(STORAGE_KEY);
  if (!saved) return null;
  try {
    const parsed = JSON.parse(saved) as ViewContext;
    // Migrate legacy unscoped key to per-user storage.
    if (!localStorage.getItem(userKey)) {
      localStorage.setItem(userKey, saved);
    }
    return parsed;
  } catch {
    return null;
  }
}

function withFreshOrganizerName(
  context: ViewContext,
  organizations: OrganizationContext[],
): ViewContext {
  if (context.type !== 'organizer') return context;
  const org = organizations.find((o) => o.id === context.organizationId);
  if (!org || org.name === context.organizationName) return context;
  return { ...context, organizationName: org.name };
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

export function RoleProvider({ children }: { children: React.ReactNode }) {
  const { user, profile, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeContext, setActiveContextState] = useState<ViewContext>({ type: 'spectator' });
  const [availableTeams, setAvailableTeams] = useState<TeamContext[]>([]);
  const [availableAthleteTeams, setAvailableAthleteTeams] = useState<AthleteTeamContext[]>([]);
  const [availableOrganizations, setAvailableOrganizations] = useState<OrganizationContext[]>([]);
  const [availableTournaments, setAvailableTournaments] = useState<TournamentContext[]>([]);
  const [availableLeagues, setAvailableLeagues] = useState<LeagueContext[]>([]);
  const [availableManagedTeams, setAvailableManagedTeams] = useState<LeagueTeamManagerContext[]>([]);
  const hasAppliedDefaultContextRef = useRef(false);
  // Track which user we last fetched contexts for, so that incidental
  // `user` reference changes (e.g. Supabase TOKEN_REFRESHED / window-focus
  // SIGNED_IN events) don't re-run refreshContexts and risk overwriting
  // the user's currently selected role.
  const lastFetchedUserIdRef = useRef<string | null>(null);

  // Save context to localStorage when it changes (scoped per logged-in user)
  const setActiveContext = useCallback((context: ViewContext) => {
    setActiveContextState(context);
    if (user?.id) {
      localStorage.setItem(getActiveContextStorageKey(user.id), JSON.stringify(context));
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(context));
    }
  }, [user?.id]);

  const isContextValid = useCallback((
    context: ViewContext,
    ids: {
      athleteTeamIds: Set<string>;
      teamIds: Set<string>;
      orgIds: Set<string>;
      tournamentIds: Set<string>;
      leagueIds: Set<string>;
      managedTeamIds: Set<string>;
    }
  ) => {
    switch (context.type) {
      case 'spectator':
        return true;
      case 'athlete':
        return ids.athleteTeamIds.has(context.teamId);
      case 'coach':
        return ids.teamIds.has(context.teamId);
      case 'organizer':
        return ids.orgIds.has(context.organizationId);
      case 'tournament_staff':
        return ids.tournamentIds.has(context.tournamentId);
      case 'league_manager':
        return ids.leagueIds.has(context.leagueId);
      case 'league_team_manager':
        return ids.managedTeamIds.has(context.leagueTeamId);
      default:
        return false;
    }
  }, []);

  // Fetch all available contexts for the user
  const refreshContexts = useCallback(async () => {
    if (!user) {
      setAvailableTeams([]);
      setAvailableAthleteTeams([]);
      setAvailableOrganizations([]);
      setAvailableTournaments([]);
      setAvailableLeagues([]);
      setAvailableManagedTeams([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // Fetch team memberships
      const { data: teamMembers } = await supabase
        .from('team_members')
        .select(`
          role,
          teams:team_id (
            id,
            slug,
            name,
            logo_url
          )
        `)
        .eq('user_id', user.id);

      // Also fetch teams where user is coach_id (legacy)
      const { data: coachedTeams } = await supabase
        .from('teams')
        .select('id, slug, name, logo_url')
        .eq('coach_id', user.id);

      // Fetch participant records to link athlete to their participant_id
      const { data: participantRecords } = await supabase
        .from('participants')
        .select('id, team_id')
        .eq('user_id', user.id);

      // Fetch organization memberships
      const { data: orgMembers } = await supabase
        .from('organization_members')
        .select(`
          role,
          organizations:organization_id (
            id,
            name,
            logo_url
          )
        `)
        .eq('user_id', user.id);

      // Also fetch orgs where user is owner
      const { data: ownedOrgs } = await supabase
        .from('organizations')
        .select('id, name, logo_url')
        .eq('owner_id', user.id);

      // Fetch tournament staff roles (only for active tournaments)
      const { data: tournamentStaff } = await supabase
        .from('tournament_staff')
        .select(`
          role,
          tournaments:tournament_id (
            id,
            name,
            status,
            organizations:organization_id (
              name
            )
          )
        `)
        .eq('user_id', user.id);

      // Fetch league team manager roles
      const { data: leagueTeamManagerData } = await supabase
        .from('league_team_managers')
        .select(`
          id,
          role,
          can_manage_roster,
          can_register_tournaments,
          can_view_financials,
          league_teams:league_team_id (
            id,
            teams:team_id (
              id,
              slug,
              name,
              logo_url
            ),
            leagues:league_id (
              id,
              name,
              slug,
              status
            )
          )
        `)
        .eq('user_id', user.id);

      // Fetch leagues the user manages (from their organizations)
      // Note: leagues don't have a created_by field, they belong to organizations
      const userLeagues: any[] = [];

      // Also fetch leagues from user's organizations
      let orgLeagues: any[] = [];
      if (ownedOrgs && ownedOrgs.length > 0) {
        const orgIds = ownedOrgs.map(o => o.id);
        const { data: leaguesFromOrgs } = await supabase
          .from('leagues')
          .select(`
            id,
            name,
            slug,
            status,
            logo_url,
            organizations:organization_id (
              name
            )
          `)
          .in('organization_id', orgIds)
          .neq('status', 'archived');
        
        if (leaguesFromOrgs) {
          orgLeagues = leaguesFromOrgs;
        }
      }

      // Build teams list (coach/manager roles) and athlete teams separately
      const teams: TeamContext[] = [];
      const athleteTeams: AthleteTeamContext[] = [];
      const teamIds = new Set<string>();
      const athleteTeamIds = new Set<string>();

      // Create a map of team_id to participant_id
      const participantMap = new Map<string, string>();
      if (participantRecords) {
        participantRecords.forEach((p) => {
          if (p.team_id) {
            participantMap.set(p.team_id, p.id);
          }
        });
      }

      if (teamMembers) {
        teamMembers.forEach((tm: any) => {
          if (tm.teams) {
            // Athletes go to athlete teams
            if (tm.role === 'athlete') {
              if (!athleteTeamIds.has(tm.teams.id)) {
                athleteTeams.push({
                  id: tm.teams.id,
                  slug: tm.teams.slug,
                  name: tm.teams.name,
                  logoUrl: tm.teams.logo_url,
                  participantId: participantMap.get(tm.teams.id),
                });
                athleteTeamIds.add(tm.teams.id);
              }
            } else {
              // Coach/manager roles
              if (!teamIds.has(tm.teams.id)) {
                teams.push({
                  id: tm.teams.id,
                  slug: tm.teams.slug,
                  name: tm.teams.name,
                  role: tm.role,
                  logoUrl: tm.teams.logo_url,
                });
                teamIds.add(tm.teams.id);
              }
            }
          }
        });
      }

      if (coachedTeams) {
        coachedTeams.forEach((team) => {
          if (!teamIds.has(team.id)) {
            teams.push({
              id: team.id,
              slug: team.slug,
              name: team.name,
              role: 'head_coach',
              logoUrl: team.logo_url,
            });
            teamIds.add(team.id);
          }
        });
      }

      // Build organizations list
      const organizations: OrganizationContext[] = [];
      const orgIds = new Set<string>();

      if (orgMembers) {
        orgMembers.forEach((om: any) => {
          if (om.organizations && !orgIds.has(om.organizations.id)) {
            organizations.push({
              id: om.organizations.id,
              name: resolveOrganizationDisplayName(
                { name: om.organizations.name, role: om.role },
                profile,
              ),
              role: om.role,
              logoUrl: om.organizations.logo_url,
            });
            orgIds.add(om.organizations.id);
          }
        });
      }

      if (ownedOrgs) {
        ownedOrgs.forEach((org) => {
          if (!orgIds.has(org.id)) {
            organizations.push({
              id: org.id,
              name: resolveOrganizationDisplayName({ name: org.name, role: 'owner' }, profile),
              role: 'owner',
              logoUrl: org.logo_url,
            });
            orgIds.add(org.id);
          }
        });
      }

      // Build tournaments list (only active ones)
      const tournaments: TournamentContext[] = [];
      const tournamentIds = new Set<string>();

      if (tournamentStaff) {
        tournamentStaff.forEach((ts: any) => {
          if (ts.tournaments && !tournamentIds.has(ts.tournaments.id)) {
            // Only include non-completed tournaments
            if (ts.tournaments.status !== 'completed' && ts.tournaments.status !== 'cancelled') {
              tournaments.push({
                id: ts.tournaments.id,
                name: ts.tournaments.name,
                role: ts.role,
                organizationName: ts.tournaments.organizations?.name,
              });
              tournamentIds.add(ts.tournaments.id);
            }
          }
        });
      }

      // Build leagues list
      const leagues: LeagueContext[] = [];
      const leagueIds = new Set<string>();

      // Add user-created leagues
      if (userLeagues) {
        userLeagues.forEach((league: any) => {
          if (!leagueIds.has(league.id)) {
            leagues.push({
              id: league.id,
              name: league.name,
              slug: league.slug,
              status: league.status,
              logoUrl: league.logo_url,
              organizationName: league.organizations?.name,
            });
            leagueIds.add(league.id);
          }
        });
      }

      // Add leagues from user's organizations
      if (orgLeagues) {
        orgLeagues.forEach((league: any) => {
          if (!leagueIds.has(league.id)) {
            leagues.push({
              id: league.id,
              name: league.name,
              slug: league.slug,
              status: league.status,
              logoUrl: league.logo_url,
              organizationName: league.organizations?.name,
            });
            leagueIds.add(league.id);
          }
        });
      }

      // Build managed teams list (league team managers)
      const managedTeams: LeagueTeamManagerContext[] = [];
      const managedTeamIds = new Set<string>();

      if (leagueTeamManagerData) {
        leagueTeamManagerData.forEach((ltm: any) => {
          if (ltm.league_teams && !managedTeamIds.has(ltm.league_teams.id)) {
            const leagueTeam = ltm.league_teams;
            // Only include if league is not archived
            if (leagueTeam.leagues?.status !== 'archived') {
              managedTeams.push({
                leagueTeamId: leagueTeam.id,
                leagueId: leagueTeam.leagues?.id,
                leagueName: leagueTeam.leagues?.name || 'Unknown League',
                teamId: leagueTeam.teams?.id,
                teamName: leagueTeam.teams?.name || 'Unknown Team',
                role: ltm.role,
                canManageRoster: ltm.can_manage_roster,
                canRegisterTournaments: ltm.can_register_tournaments,
                canViewFinancials: ltm.can_view_financials,
                teamLogoUrl: leagueTeam.teams?.logo_url,
              });
              managedTeamIds.add(leagueTeam.id);
            }
          }
        });
      }

      setAvailableTeams(teams);
      setAvailableAthleteTeams(athleteTeams);
      setAvailableOrganizations(organizations);
      setAvailableTournaments(tournaments);
      setAvailableLeagues(leagues);
      setAvailableManagedTeams(managedTeams);

      const ids = {
        athleteTeamIds,
        teamIds,
        orgIds,
        tournamentIds,
        leagueIds,
        managedTeamIds,
      };

      // Only fall back to spectator when we actually have data to compare
      // against — otherwise a transient empty result (e.g. a query that
      // failed during a token refresh) would wipe out the user's chosen
      // role unexpectedly.
      const hasAnyMembershipData =
        athleteTeamIds.size > 0 ||
        teamIds.size > 0 ||
        orgIds.size > 0 ||
        tournamentIds.size > 0 ||
        leagueIds.size > 0 ||
        managedTeamIds.size > 0;

      // Prefer the user's last manually selected role over the login default.
      const savedActive = readSavedActiveContext(user.id);

      if (savedActive && isContextValid(savedActive, ids)) {
        setActiveContext(withFreshOrganizerName(savedActive, organizations));
        hasAppliedDefaultContextRef.current = true;
        setLoading(false);
        return;
      }

      if (savedActive && !isContextValid(savedActive, ids)) {
        localStorage.removeItem(getActiveContextStorageKey(user.id));
      }

      if (
        activeContext.type !== 'spectator' &&
        hasAnyMembershipData &&
        !isContextValid(activeContext, ids)
      ) {
        setActiveContext({ type: 'spectator' });
      }

      // Apply login default only when there is no valid last-selected role.
      if (!hasAppliedDefaultContextRef.current) {
        let parsedDefault: ViewContext | null = null;
        const savedDefault = localStorage.getItem(DEFAULT_STORAGE_KEY);

        if (savedDefault) {
          try {
            parsedDefault = JSON.parse(savedDefault) as ViewContext;
          } catch {
            // Invalid localStorage default context, ignore and check profile fallback.
          }
        }

        if (!parsedDefault && profile?.default_login_context) {
          parsedDefault = profile.default_login_context as ViewContext;
          localStorage.setItem(DEFAULT_STORAGE_KEY, JSON.stringify(parsedDefault));
        }

        if (parsedDefault && isContextValid(parsedDefault, ids)) {
          setActiveContext(withFreshOrganizerName(parsedDefault, organizations));
        }

        hasAppliedDefaultContextRef.current = true;
      }

      if (activeContext.type === 'organizer') {
        const refreshed = withFreshOrganizerName(activeContext, organizations);
        if (
          refreshed.type === 'organizer' &&
          refreshed.organizationName !== activeContext.organizationName
        ) {
          setActiveContext(refreshed);
        }
      }
    } catch (error) {
      console.error('Error fetching user contexts:', error);
    }

    setLoading(false);
  }, [user, profile, activeContext, setActiveContext, isContextValid]);

  // Fetch contexts when the authenticated user actually changes.
  // We intentionally key on `user?.id` rather than the `user` object so
  // that Supabase auth events that don't change identity (TOKEN_REFRESHED,
  // SIGNED_IN re-fires when the tab regains focus, etc.) do not re-run
  // refreshContexts — those re-runs were silently resetting the user's
  // active role to spectator when they left and returned to the window.
  useEffect(() => {
    const currentUserId = user?.id ?? null;

    if (isAuthenticated && currentUserId) {
      if (lastFetchedUserIdRef.current === currentUserId) {
        return;
      }
      lastFetchedUserIdRef.current = currentUserId;
      refreshContexts();
    } else {
      lastFetchedUserIdRef.current = null;
      setAvailableTeams([]);
      setAvailableAthleteTeams([]);
      setAvailableOrganizations([]);
      setAvailableTournaments([]);
      setAvailableLeagues([]);
      setAvailableManagedTeams([]);
      setActiveContextState({ type: 'spectator' });
      hasAppliedDefaultContextRef.current = false;
      setLoading(false);
    }
  }, [user?.id, isAuthenticated]); // Intentionally not including refreshContexts to avoid loops

  // Computed values
  const isViewingAsSpectator = activeContext.type === 'spectator';
  const isViewingAsAthlete = activeContext.type === 'athlete';
  const isViewingAsCoach = activeContext.type === 'coach';
  const isViewingAsOrganizer = activeContext.type === 'organizer';
  const isViewingAsTournamentStaff = activeContext.type === 'tournament_staff';
  const isViewingAsLeagueManager = activeContext.type === 'league_manager';
  const isViewingAsLeagueTeamManager = activeContext.type === 'league_team_manager';

  const currentAthleteTeam = useMemo(() => {
    if (activeContext.type === 'athlete') {
      return availableAthleteTeams.find(t => t.id === activeContext.teamId) || null;
    }
    return null;
  }, [activeContext, availableAthleteTeams]);

  const currentTeam = useMemo(() => {
    if (activeContext.type === 'coach') {
      return availableTeams.find(t => t.id === activeContext.teamId) || null;
    }
    return null;
  }, [activeContext, availableTeams]);

  const currentOrganization = useMemo(() => {
    if (activeContext.type === 'organizer') {
      return availableOrganizations.find(o => o.id === activeContext.organizationId) || null;
    }
    return null;
  }, [activeContext, availableOrganizations]);

  const currentTournament = useMemo(() => {
    if (activeContext.type === 'tournament_staff') {
      return availableTournaments.find(t => t.id === activeContext.tournamentId) || null;
    }
    return null;
  }, [activeContext, availableTournaments]);

  const currentLeague = useMemo(() => {
    if (activeContext.type === 'league_manager') {
      return availableLeagues.find(l => l.id === activeContext.leagueId) || null;
    }
    return null;
  }, [activeContext, availableLeagues]);

  const currentManagedTeam = useMemo(() => {
    if (activeContext.type === 'league_team_manager') {
      return availableManagedTeams.find(t => t.leagueTeamId === activeContext.leagueTeamId) || null;
    }
    return null;
  }, [activeContext, availableManagedTeams]);

  const hasAnyManagementRole = availableTeams.length > 0 || 
    availableOrganizations.length > 0 || 
    availableTournaments.length > 0 ||
    availableLeagues.length > 0 ||
    availableManagedTeams.length > 0;

  const isAthlete = availableAthleteTeams.length > 0;

  const hasLeagues = availableLeagues.length > 0;

  const hasManagedTeams = availableManagedTeams.length > 0;

  return (
    <RoleContext.Provider
      value={{
        activeContext,
        setActiveContext,
        availableTeams,
        availableAthleteTeams,
        availableOrganizations,
        availableTournaments,
        availableLeagues,
        availableManagedTeams,
        loading,
        isViewingAsSpectator,
        isViewingAsAthlete,
        isViewingAsCoach,
        isViewingAsOrganizer,
        isViewingAsTournamentStaff,
        isViewingAsLeagueManager,
        isViewingAsLeagueTeamManager,
        currentAthleteTeam,
        currentTeam,
        currentOrganization,
        currentTournament,
        currentLeague,
        currentManagedTeam,
        refreshContexts,
        hasAnyManagementRole,
        isAthlete,
        hasLeagues,
        hasManagedTeams,
      }}
    >
      {children}
    </RoleContext.Provider>
  );
}

export function useRole() {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
}

